// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import { Rnd } from 'react-rnd';
import '../../styles/element-component.css';

interface ElementComponentProps {
  element: any;
  isSelected: boolean;
  isEditing: boolean;
  onClick: () => void;
  scale: number;
}

const ElementComponent: React.FC<ElementComponentProps> = ({
  element,
  isSelected,
  isEditing,
  onClick,
  scale
}) => {
  const { updateElement } = useRestaurant();

  const getTopLeft = useCallback((center: { x: number; y: number }, dims: { width: number; height: number }) => ({
    x: center.x - dims.width / 2,
    y: center.y - dims.height / 2
  }), []);

  const getCenter = useCallback((topLeft: { x: number; y: number }, dims: { width: number; height: number }) => ({
    x: topLeft.x + dims.width / 2,
    y: topLeft.y + dims.height / 2
  }), []);

  const initialSize = { width: element.width, height: element.height };
  const [size, setSize] = useState(initialSize);
  const [position, setPosition] = useState(getTopLeft(element.position, initialSize));

  useEffect(() => {
    const newSize = { width: element.width, height: element.height };
    const newPosition = getTopLeft(element.position, newSize);
    setSize(prev => {
      if (prev.width === newSize.width && prev.height === newSize.height) {
        return prev;
      }
      return newSize;
    });
    setPosition(prev => {
      if (prev.x === newPosition.x && prev.y === newPosition.y) {
        return prev;
      }
      return newPosition;
    });
  }, [element.position, element.position.x, element.position.y, element.width, element.height, getTopLeft]);

  const handleDragStop = (_e: any, d: any) => {
    const newPosition = { x: d.x, y: d.y };
    setPosition(newPosition);
    updateElement(element.id, { position: getCenter(newPosition, size) });
  };

  const handleResizeStop = (_e: any, _direction: any, ref: any, _delta: any, newPosition: any) => {
    const newSize = {
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height)
    };
    const updatedTopLeft = { x: newPosition.x, y: newPosition.y };
    setSize(newSize);
    setPosition(updatedTopLeft);

    updateElement(element.id, {
      position: getCenter(updatedTopLeft, newSize),
      width: newSize.width,
      height: newSize.height
    });
  };
  
  const getElementStyle = () => {
    if (element.type === 'door') {
      return {
        backgroundColor: '#e8d9b5',
        border: '2px solid #a58d72',
        borderRadius: '4px'
      };
    }

    // Use custom color if available, otherwise use default colors
    if (element.color) {
      return {
        backgroundColor: element.color,
        border: '2px solid #666',
        borderRadius: element.shape === 'circle' ? '50%' : '4px'
      };
    }

    // Default colors for objects without custom color
    switch (element.shape) {
      case 'circle':
        return {
          backgroundColor: '#e9c46a',
          border: '2px solid #b06b33',
          borderRadius: '50%'
        };
      case 'rectangle':
        return {
          backgroundColor: '#e76f51',
          border: '2px solid #9e4533',
          borderRadius: '4px'
        };
      case 'square':
      default:
        return {
          backgroundColor: '#2a9d8f',
          border: '2px solid #1e6e64',
          borderRadius: '4px'
        };
    }
  };
  
  return (
    <Rnd
      position={position}
      size={size}
      scale={scale}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      minWidth={40}
      minHeight={40}
      disableDragging={!isEditing}
      enableResizing={isEditing}
      className={`element-component ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
    >
      <div className="element-content" style={getElementStyle()}>
        <span className="element-name" style={{ color: element.textColor || '#000000' }}>{element.name}</span>
      </div>
    </Rnd>
  );
};

export default ElementComponent;
